import axios from 'axios';

/**
 * Maps a failed login mutation's error into a user-facing message. Rate-limiting (429,
 * error.code === 'RATE_LIMITED' from auth-service's authRateLimiter) and actual wrong credentials
 * (401, UNAUTHORIZED) used to collapse into the same generic "Invalid email or password" text in
 * every theme's LoginPage, leaving a rate-limited user with no signal that retrying immediately
 * won't help. Found while load-testing login across multiple accounts in a short window.
 *
 * Deliberately does NOT distinguish "wrong password" from "unknown email" beyond this generic
 * message -- auth-service intentionally collapses both into the same UNAUTHORIZED response to
 * prevent account enumeration. Do not "improve" this by surfacing a more specific message here.
 */
export function getLoginErrorMessage(error: unknown): string {
  if (!error) return '';
  if (axios.isAxiosError(error)) {
    const code = error.response?.data?.error?.code;
    const message = error.response?.data?.error?.message;
    if (code === 'RATE_LIMITED') {
      return message || 'Too many login attempts. Please wait a few minutes and try again.';
    }
  }
  return 'Invalid email or password. Please try again.';
}

/**
 * General-purpose mapper for register/forgot-password/reset-password/OTP flows, where -- unlike
 * login -- auth-service's error responses already carry a specific, safe-to-show message (e.g.
 * "A user with this email already exists", "Invalid or expired reset token", "Password must
 * contain at least one uppercase letter"). Every auth-service error is shaped as
 * `{ error: { code, message, details? } }` (see errorHandler.ts) -- `details` is populated only
 * for Zod validation failures, as `[{ field, message }]`, in which case `error.message` itself is
 * just the generic "Validation failed" and the real, specific text is `details[0].message`.
 *
 * Several pages previously read `err.response?.data?.message` (no `.error` nesting), which never
 * matched this shape and silently fell back to their generic default every time.
 */
export function getAuthErrorMessage(error: unknown, fallback: string): string {
  if (!error) return '';
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Can't reach the server — please check your connection and try again.";
    }
    const { status, data } = error.response;
    const code = data?.error?.code;
    const details = data?.error?.details as Array<{ field?: string; message?: string }> | undefined;
    const message = data?.error?.message;

    if (code === 'RATE_LIMITED') {
      return message || 'Too many attempts. Please wait a few minutes and try again.';
    }
    if (status >= 500) {
      return 'Something went wrong on our end. Please try again shortly.';
    }
    if (details?.[0]?.message) return details[0].message;
    if (message) return message;
  }
  return fallback;
}

export const getRegisterErrorMessage = (error: unknown): string =>
  getAuthErrorMessage(error, 'Registration failed. Please try again.');

export const getForgotPasswordErrorMessage = (error: unknown): string =>
  getAuthErrorMessage(error, 'Could not send reset email. Please try again.');

export const getResetPasswordErrorMessage = (error: unknown): string =>
  getAuthErrorMessage(error, 'Failed to reset password. The link may have expired.');

export const getOtpErrorMessage = (error: unknown, fallback: string): string =>
  getAuthErrorMessage(error, fallback);
